/**
 * Main Widget Component
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import * as store from '../store';
import * as api from '../api';
import type { WidgetConfig, Conversation, Message } from '../types';
import { ChatIcon, CloseIcon, SendIcon, MessageCircleIcon, ChevronLeftIcon, PlusIcon, ImageIcon, XIcon, MaximizeIcon } from './Icons';
import { emitWidgetAnalytics } from '../analytics';
import { SingleFlight } from '../single-flight';
import { getThumbnailUrl } from '../image-links';
import { RefreshableImage } from './RefreshableImage';

interface Props {
  config: WidgetConfig;
}

type View = 'chat' | 'conversations';
type StarterAnswerKind = 'price' | 'material';

interface ConversationStarter {
  label: string;
  prompt: string;
  answerKind?: StarterAnswerKind;
}

// Allowed image types and max size
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// Image compression settings
const MAX_IMAGE_DIMENSION = 1200; // Max width or height for upload
const COMPRESSION_QUALITY = 0.8; // JPEG quality (0-1)

// Conversation starters - shown as buttons in empty chat state
const CONVERSATION_STARTERS: readonly ConversationStarter[] = [
  { label: 'Get a Price Quote', prompt: 'I\'d like to get a price quote for bulk materials delivery.', answerKind: 'price' },
  { label: 'Help Me Choose a Material', prompt: 'I need help choosing the right material for my project.', answerKind: 'material' },
  { label: 'Delivery & Scheduling', prompt: 'I have questions about delivery options and scheduling.' },
  { label: 'I Have a Question', prompt: 'I have a question about your products and services.' },
] as const;

const PREVIEW_STARTERS: readonly ConversationStarter[] = [
  { label: 'Get a price', prompt: CONVERSATION_STARTERS[0].prompt, answerKind: 'price' },
  { label: 'Choose a material', prompt: CONVERSATION_STARTERS[1].prompt, answerKind: 'material' },
  { label: 'Ask a question', prompt: CONVERSATION_STARTERS[3].prompt },
] as const;

const STARTER_ANSWERS: Record<StarterAnswerKind, readonly string[]> = {
  price: ['Gravel', 'Sand', 'Soil', 'Mulch', 'Not sure'],
  material: ['Driveway', 'Drainage', 'Garden', 'Leveling', 'Other'],
};

function getStarterAnswers(kind: StarterAnswerKind | null, message: Message | undefined): readonly string[] {
  if (!kind || !message?.isAiResponse || message.senderType !== 'agent') return [];

  const answers = STARTER_ANSWERS[kind];
  const content = message.content.toLowerCase();
  return answers.slice(0, -1).filter((answer) => content.includes(answer.toLowerCase())).length >= 2
    ? answers
    : [];
}

/**
 * Compress an image using canvas
 * Reduces dimensions and file size for faster upload
 */
async function compressImage(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width > height) {
          height = (height / width) * MAX_IMAGE_DIMENSION;
          width = MAX_IMAGE_DIMENSION;
        } else {
          width = (width / height) * MAX_IMAGE_DIMENSION;
          height = MAX_IMAGE_DIMENSION;
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl); // Fallback to original
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with compression (smaller than PNG)
      const compressedDataUrl = canvas.toDataURL('image/jpeg', COMPRESSION_QUALITY);

      console.log(`[Image] Compressed: ${Math.round(dataUrl.length / 1024)}KB → ${Math.round(compressedDataUrl.length / 1024)}KB`);
      resolve(compressedDataUrl);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = dataUrl;
  });
}

/**
 * Simple markdown renderer for chat messages.
 * Supports: **bold**, *italic*, newlines, and safe web links
 */
function renderMarkdown(text: string) {
  const parts: (string | any)[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|\n|https?:\/\/[^\s<]+)/g;
  let lastIndex = 0;
  let m;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    if (m[0] === '\n') {
      parts.push(<br />);
    } else if (m[2]) {
      parts.push(<strong>{m[2]}</strong>);
    } else if (m[3]) {
      parts.push(<em>{m[3]}</em>);
    } else {
      const url = m[0].replace(/[),.!?;:]+$/, '');
      const trailing = m[0].slice(url.length);

      parts.push(
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      );
      if (trailing) parts.push(trailing);
    }
    lastIndex = m.index + m[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

export function Widget({ config }: Props) {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [view, setView] = useState<View>('chat');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // The picture open in the enlarged view; its link follows the message, so a refreshed link replaces it.
  const [expandedImage, setExpandedImage] = useState<{ messageId: string; url: string } | null>(null);
  const [starterSending, setStarterSending] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [starterAnswerKind, setStarterAnswerKind] = useState<StarterAnswerKind | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const firstMessageAttemptedRef = useRef(false);
  const previousOpenRef = useRef(store.isOpen.value);
  const previewShownRef = useRef(false);
  const initializationRef = useRef<SingleFlight | null>(null);
  const actionInFlightRef = useRef(false);

  if (!initializationRef.current) {
    initializationRef.current = new SingleFlight();
  }

  const serverConfig = store.serverConfig.value;
  const position = serverConfig?.position || config.position || 'bottom-left';
  const positionClass = position === 'bottom-left' ? 'left' : 'right';
  const previewText = serverConfig?.bubbleText || config.bubbleText;

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 479);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  function ensureInitialized(): Promise<void> {
    return initializationRef.current!.run(async () => {
      try {
        await api.init();
        store.setError(null);
      } catch (err) {
        console.error('Widget init failed:', err);
        store.setError('Failed to connect to chat');
        store.setLoading(false);
        throw err;
      }
    });
  }

  // The API client is configured synchronously by init() before this component renders.
  useEffect(() => {
    // Auto-open after delay if configured — init lazily when auto-open fires
    if (config.autoOpenDelayMs) {
      autoOpenTimeoutRef.current = setTimeout(async () => {
        if (!store.isOpen.value && store.messages.value.length === 0) {
          try {
            await ensureInitialized();
            store.openWidget();
          } catch {
            // The inline error state allows the visitor to retry manually.
          }
        }
      }, config.autoOpenDelayMs);
    }

    // Cleanup on unmount
    return () => {
      api.disconnect();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (autoOpenTimeoutRef.current) {
        clearTimeout(autoOpenTimeoutRef.current);
      }
    };
  }, []);

  // Track open/close transitions without coupling the widget to a provider.
  useEffect(() => {
    if (store.isOpen.value === previousOpenRef.current) return;
    previousOpenRef.current = store.isOpen.value;

    emitWidgetAnalytics(store.isOpen.value ? 'chuck_widget_opened' : 'chuck_widget_closed', {
      project_id: config.projectId,
      source: 'widget_ui',
    });

    if (store.isOpen.value) {
      void ensureInitialized().then(() => api.setRealtimeActive(true)).catch(() => {});
    } else {
      void api.setRealtimeActive(store.awaitingResponse.value);
    }
  }, [store.isOpen.value]);

  useEffect(() => {
    if (!previewText || previewDismissed || store.isOpen.value || previewShownRef.current) {
      return;
    }

    previewShownRef.current = true;
    emitWidgetAnalytics('chuck_widget_preview_shown', {
      project_id: config.projectId,
    });
  }, [previewText, previewDismissed, store.isOpen.value]);

  // Delayed typing indicator - show after 2 seconds
  useEffect(() => {
    if (isSending) {
      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Set timeout to show typing after 2 seconds
      typingTimeoutRef.current = setTimeout(() => {
        setShowTyping(true);
      }, 2000);
    } else {
      // Clear timeout and hide typing when done
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      setShowTyping(false);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [isSending]);

  // Scroll to bottom when messages change or typing status changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    // Reset starterSending once messages arrive (AI response came through)
    if (starterSending && store.messages.value.length > 0) {
      setStarterSending(false);
    }
  }, [store.messages.value, store.isTyping.value, showTyping]);

  // Mark messages as read, reset tab title, and scroll to bottom when widget opens
  useEffect(() => {
    if (store.isOpen.value) {
      api.markRead().catch(() => {});
      api.resetTabTitle();
      // Scroll to bottom when widget opens
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [store.isOpen.value]);

  // Start 5-minute countdown when human transfer is requested
  useEffect(() => {
    if (store.humanHandling.value && store.transferCountdown.value === 0 && !store.humanHandlerName.value) {
      // Add transfer notification message directly in the widget
      store.addMessage({
        id: `transfer-${Date.now()}`,
        content: "You're being connected to a team member. Hang tight — someone should be with you within the next few minutes!",
        contentType: 'text',
        senderType: 'agent',
        senderName: null,
        isAiResponse: true,
        createdAt: new Date().toISOString(),
      });

      // Start countdown
      store.transferCountdown.value = 300; // 5 minutes

      countdownRef.current = setInterval(() => {
        if (store.transferCountdown.value <= 1) {
          store.transferCountdown.value = 0;
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
        } else {
          store.transferCountdown.value -= 1;
        }
      }, 1000);
    }

    // Agent joined - cancel countdown
    if (store.humanHandlerName.value && countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
      store.transferCountdown.value = 0;
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [store.humanHandling.value, store.humanHandlerName.value]);

  // Load conversations when switching to conversations view
  useEffect(() => {
    if (view === 'conversations') {
      loadConversations();
    }
  }, [view]);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const convos = await api.getConversations();
      setConversations(convos);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  // Handle image selection
  const handleImageSelect = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      store.setError('Only images (JPEG, PNG, GIF, WebP) are allowed');
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      store.setError('Image must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        // Compress the image before preview/upload
        const compressed = await compressImage(reader.result as string);
        setSelectedImage(compressed);
      } catch (err) {
        console.error('Image compression failed:', err);
        // Fall back to original
        setSelectedImage(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  // Clear selected image
  const clearSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle send message
  const handleSend = async () => {
    if ((!inputValue.trim() && !selectedImage) || actionInFlightRef.current) return;

    actionInFlightRef.current = true;
    setIsSending(true);

    if (!firstMessageAttemptedRef.current) {
      firstMessageAttemptedRef.current = true;
      emitWidgetAnalytics('chuck_first_message_attempted', {
        project_id: config.projectId,
        content_type: selectedImage ? 'image' : 'text',
        has_caption: Boolean(selectedImage && inputValue.trim()),
      });
    }

    const content = inputValue.trim();
    const image = selectedImage;
    const previousStarterAnswerKind = starterAnswerKind;
    setStarterAnswerKind(null);

    try {
      await ensureInitialized();
      setInputValue('');

      if (image) {
        // Send image WITH text caption as ONE combined message
        // This ensures the AI sees both the image and the question together
        await api.sendImageWithCaption(image, content || undefined);
        clearSelectedImage();
      } else if (content) {
        // Text only message
        await api.sendMessage(content);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      store.setError('Failed to send message');
      setStarterAnswerKind(previousStarterAnswerKind);
    } finally {
      actionInFlightRef.current = false;
      setIsSending(false);
      // Refocus the input field after sending
      textInputRef.current?.focus();
    }
  };

  // Handle conversation starter button click (no visitor bubble shown)
  const handleStarterClick = async (prompt: string, answerKind?: StarterAnswerKind) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setIsSending(true);
    if (!firstMessageAttemptedRef.current) {
      firstMessageAttemptedRef.current = true;
      emitWidgetAnalytics('chuck_first_message_attempted', {
        project_id: config.projectId,
        content_type: 'starter',
      });
    }
    try {
      await ensureInitialized();
      setStarterAnswerKind(answerKind || null);
      setStarterSending(true);
      await api.sendStarterMessage(prompt);
    } catch (err) {
      console.error('Failed to send starter message:', err);
      store.setError('Failed to send message');
      setStarterAnswerKind(null);
      setStarterSending(false); // Show starters again on error
    } finally {
      actionInFlightRef.current = false;
      setIsSending(false);
    }
  };

  const handleStarterAnswerClick = async (answer: string) => {
    if (!starterAnswerKind || actionInFlightRef.current) return;

    const answerKind = starterAnswerKind;
    actionInFlightRef.current = true;
    setStarterAnswerKind(null);
    setIsSending(true);
    emitWidgetAnalytics('chuck_starter_answer_clicked', {
      project_id: config.projectId,
      starter: answerKind,
      answer,
    });

    try {
      await ensureInitialized();
      await api.sendMessage(answer);
    } catch (err) {
      console.error('Failed to send starter answer:', err);
      store.setError('Failed to send message');
      setStarterAnswerKind(answerKind);
    } finally {
      actionInFlightRef.current = false;
      setIsSending(false);
      textInputRef.current?.focus();
    }
  };

  const openFromBubble = (source: 'bubble' | 'preview') => {
    if (source === 'preview') {
      emitWidgetAnalytics('chuck_widget_preview_clicked', {
        project_id: config.projectId,
        action: 'open_chat',
      });
    }
    setPreviewDismissed(true);
    store.openWidget();
    void ensureInitialized().catch(() => {});
  };

  const handlePreviewStarterClick = (starter: typeof PREVIEW_STARTERS[number]) => {
    emitWidgetAnalytics('chuck_widget_preview_clicked', {
      project_id: config.projectId,
      action: 'quick_reply',
      quick_reply: starter.label,
    });
    setPreviewDismissed(true);
    store.openWidget();
    void handleStarterClick(starter.prompt, starter.answerKind);
  };

  const dismissPreview = () => {
    setPreviewDismissed(true);
    emitWidgetAnalytics('chuck_widget_preview_dismissed', {
      project_id: config.projectId,
    });
  };

  // Handle enter key
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle start new conversation
  const handleNewConversation = async () => {
    try {
      await api.startNewConversation();
      setStarterAnswerKind(null);
      setView('chat');
    } catch (err) {
      console.error('Failed to start new conversation:', err);
    }
  };

  // Handle select conversation
  const handleSelectConversation = async (conversationId: string) => {
    try {
      await api.switchConversation(conversationId);
      setStarterAnswerKind(null);
      setView('chat');
    } catch (err) {
      console.error('Failed to switch conversation:', err);
    }
  };

  const isOnline = store.isConnected.value;
  const starterAnswers = getStarterAnswers(
    starterAnswerKind,
    store.messages.value[store.messages.value.length - 1]
  );

  return (
    <div class="chuck-widget">
      {/* Chat Window */}
      {store.isOpen.value && (
        <div class={`chuck-window ${positionClass}${isMobile ? ' mobile' : ''}`}>
          {/* Header */}
          <div class="chuck-header">
            <div class="chuck-header-info">
              {view === 'conversations' ? (
                <button
                  class="chuck-header-back"
                  onClick={() => setView('chat')}
                  aria-label="Back to chat"
                >
                  <ChevronLeftIcon />
                </button>
              ) : store.humanHandling.value && store.humanHandlerName.value ? (
                <div class="chuck-avatar">
                  <div class="chuck-avatar-initial" style={{ background: '#4f46e5' }}>
                    {store.humanHandlerName.value.charAt(0).toUpperCase()}
                  </div>
                  {isOnline && <span class="chuck-online-indicator" />}
                </div>
              ) : (
                <div class="chuck-avatar">
                  <img
                    src={config.avatarUrl || 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIyMCIgZmlsbD0iI2ZmNjgwMSIvPjx0ZXh0IHg9IjIwIiB5PSIyNiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE4IiBmb250LXdlaWdodD0iYm9sZCIgZmlsbD0id2hpdGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkM8L3RleHQ+PC9zdmc+'}
                    alt="Chuck"
                    class="chuck-avatar-img"
                  />
                  {isOnline && <span class="chuck-online-indicator" />}
                </div>
              )}
              <div>
                <div class="chuck-header-title">
                  {view === 'conversations'
                    ? 'Conversations'
                    : store.humanHandling.value
                      ? (store.humanHandlerName.value || 'Live Chat')
                      : 'Chuck'}
                </div>
                <div class="chuck-header-subtitle">
                  {view === 'conversations'
                    ? 'Your chat history'
                    : store.humanHandling.value
                      ? `Speaking with ${store.humanHandlerName.value || 'a team member'}`
                      : isOnline
                        ? 'Online • We typically reply instantly'
                        : 'Leave a message'}
                </div>
              </div>
            </div>
            <div class="chuck-header-actions">
              {view === 'chat' && (
                <button
                  class="chuck-header-menu"
                  onClick={() => setView('conversations')}
                  aria-label="View conversations"
                  title="View conversations"
                >
                  <MessageCircleIcon />
                </button>
              )}
              <button
                class="chuck-header-close"
                onClick={() => store.closeWidget()}
                aria-label="Close chat"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Conversations List View */}
          {view === 'conversations' ? (
            <div class="chuck-conversations">
              <button class="chuck-new-conversation" onClick={handleNewConversation}>
                <PlusIcon />
                <span>Start new conversation</span>
              </button>

              {loadingConversations ? (
                <div class="chuck-loading">
                  <div class="chuck-loading-spinner" />
                </div>
              ) : conversations.length === 0 ? (
                <div class="chuck-empty">
                  <p>No previous conversations</p>
                </div>
              ) : (
                <div class="chuck-conversation-list">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      class={`chuck-conversation-item ${conv.id === store.conversationId.value ? 'active' : ''}`}
                      onClick={() => handleSelectConversation(conv.id)}
                    >
                      <div class="chuck-conversation-preview">
                        <span class="chuck-conversation-date">
                          {formatDate(conv.lastMessageAt || conv.createdAt)}
                        </span>
                        <p class="chuck-conversation-text">
                          {conv.lastMessagePreview || 'No messages yet'}
                        </p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <span class="chuck-conversation-badge">{conv.unreadCount}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div class="chuck-messages">
                {store.isLoading.value ? (
                  <div class="chuck-loading">
                    <div class="chuck-loading-spinner" />
                  </div>
                ) : store.messages.value.length === 0 && !starterSending ? (
                  <div class="chuck-welcome">
                    <div class="chuck-welcome-icon">
                      <MessageCircleIcon />
                    </div>
                    <div class="chuck-welcome-text">
                      {serverConfig?.welcomeMessage || config.welcomeMessage || 'Hi there! How can we help you today?'}
                    </div>
                    <div class="chuck-starters">
                      {CONVERSATION_STARTERS.map((starter) => (
                        <button
                          key={starter.label}
                          class="chuck-starter-btn"
                          onClick={() => handleStarterClick(starter.prompt, starter.answerKind)}
                          disabled={isSending}
                        >
                          {starter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : store.messages.value.length === 0 && starterSending ? (
                  <div class="chuck-typing">
                    <div class="chuck-typing-dots">
                      <div class="chuck-typing-dot" />
                      <div class="chuck-typing-dot" />
                      <div class="chuck-typing-dot" />
                    </div>
                    <span>Chuck is typing...</span>
                  </div>
                ) : (
                  <>
                    {store.messages.value.map((message) => (
                      <div
                        key={message.id}
                        class={`chuck-message ${message.senderType}`}
                      >
                        {message.contentType === 'image' ? (
                          <>
                            <div class="chuck-message-image" onClick={() => setExpandedImage({ messageId: message.id, url: message.content })}>
                              {/* Show thumbnail in chat, full resolution on expand; an expired link is refreshed once */}
                              <RefreshableImage
                                src={getThumbnailUrl(message.content)}
                                alt="Shared image"
                                loading="lazy"
                                onRefresh={() => api.refreshPictureLink(message.id)}
                              />
                              <div class="chuck-message-image-expand">
                                <MaximizeIcon />
                              </div>
                            </div>
                            {/* Show caption below image if present */}
                            {message.caption && message.caption !== '[Image]' && (
                              <div class="chuck-message-caption">{message.caption}</div>
                            )}
                          </>
                        ) : (
                          renderMarkdown(message.content)
                        )}
                        {message.senderType !== 'system' && (
                          <div class="chuck-message-meta">
                            {formatTime(message.createdAt)}
                            {message.senderType === 'agent' && (
                              message.isAiResponse
                                ? ' • Chuck'
                                : message.senderName
                                  ? ` • ${message.senderName}`
                                  : ''
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {starterAnswers.length > 0 && (
                      <div class="chuck-starter-answers" role="group" aria-label="Quick replies">
                        {starterAnswers.map((answer) => (
                          <button
                            key={answer}
                            class="chuck-starter-btn"
                            onClick={() => handleStarterAnswerClick(answer)}
                            disabled={isSending}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Typing indicator - show after delay or when someone is typing */}
                    {(showTyping || store.isTyping.value) && (
                      <div class="chuck-typing">
                        <div class="chuck-typing-dots">
                          <div class="chuck-typing-dot" />
                          <div class="chuck-typing-dot" />
                          <div class="chuck-typing-dot" />
                        </div>
                        <span>
                          {store.isTyping.value
                            ? `${store.typingName.value || 'Agent'} is typing...`
                            : 'Chuck is typing...'}
                        </span>
                      </div>
                    )}
                    {/* Transfer countdown timer */}
                    {store.transferCountdown.value > 0 && (
                      <div class="chuck-transfer-timer">
                        <div class="chuck-transfer-timer-icon">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </div>
                        <span>Waiting for a team member... {Math.floor(store.transferCountdown.value / 60)}:{String(store.transferCountdown.value % 60).padStart(2, '0')}</span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input */}
              <div class="chuck-input-area">
                {/* Image preview */}
                {selectedImage && (
                  <div class="chuck-image-preview">
                    <img src={selectedImage} alt="Preview" />
                    <button class="chuck-image-preview-close" onClick={clearSelectedImage}>
                      <XIcon />
                    </button>
                  </div>
                )}
                <div class="chuck-input">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                  />
                  <button
                    class="chuck-input-image"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSending}
                    aria-label="Upload image"
                  >
                    <ImageIcon />
                  </button>
                  <input
                    type="text"
                    ref={textInputRef}
                    class="chuck-input-field"
                    placeholder={serverConfig?.placeholderText || config.placeholderText || 'Type a message...'}
                    value={inputValue}
                    onInput={(e) => setInputValue((e.target as HTMLInputElement).value)}
                    onKeyDown={handleKeyDown}
                    disabled={isSending}
                  />
                  <button
                    class="chuck-input-send"
                    onClick={handleSend}
                    disabled={(!inputValue.trim() && !selectedImage) || isSending}
                    aria-label="Send message"
                  >
                    <SendIcon />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Powered By */}
          {(serverConfig?.showPoweredBy !== false && config.showPoweredBy !== false) && (
            <div class="chuck-powered">
              Powered by <a href="https://hellogravel.com" target="_blank" rel="noopener">HelloGravel</a>
            </div>
          )}
        </div>
      )}

      {previewText && !store.isOpen.value && !previewDismissed && (
        <div class={`chuck-proactive-preview ${positionClass}`} aria-live="polite">
          <div class="chuck-proactive-preview-card">
            <div class="chuck-proactive-preview-avatar" aria-hidden="true">
              {config.avatarUrl ? (
                <img src={config.avatarUrl} alt="" />
              ) : (
                <span>C</span>
              )}
            </div>
            <button
              class="chuck-proactive-preview-message"
              onClick={() => openFromBubble('preview')}
              aria-label={`Open chat: ${previewText}`}
            >
              <span class="chuck-proactive-preview-name">Chuck</span>
              <span>{previewText}</span>
            </button>
            <button
              class="chuck-proactive-preview-close"
              onClick={dismissPreview}
              aria-label="Dismiss chat invitation"
            >
              <XIcon />
            </button>
          </div>
          <div class="chuck-proactive-preview-replies" aria-label="Quick replies">
            {PREVIEW_STARTERS.map((starter) => (
              <button
                key={starter.label}
                class="chuck-proactive-preview-reply"
                onClick={() => handlePreviewStarterClick(starter)}
              >
                {starter.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bubble Button */}
      <button
        class={`chuck-bubble ${positionClass}`}
        onClick={() => {
          if (!store.isOpen.value) {
            openFromBubble('bubble');
          } else {
            store.closeWidget();
          }
        }}
        aria-label={store.isOpen.value ? 'Close chat' : 'Open chat'}
      >
        {store.isOpen.value ? <CloseIcon /> : <ChatIcon />}
        {!store.isOpen.value && store.unreadCount.value > 0 && (
          <span class="chuck-bubble-badge">
            {store.unreadCount.value > 9 ? '9+' : store.unreadCount.value}
          </span>
        )}
      </button>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div class="chuck-image-modal" onClick={() => setExpandedImage(null)}>
          <div class="chuck-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button class="chuck-image-modal-close" onClick={() => setExpandedImage(null)}>
              <XIcon />
            </button>
            <RefreshableImage
              src={store.messages.value.find((m) => m.id === expandedImage.messageId)?.content || expandedImage.url}
              alt="Expanded image"
              onRefresh={() => api.refreshPictureLink(expandedImage.messageId)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 24 * 60 * 60 * 1000) {
    return formatTime(dateString);
  }

  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
